<template>
  <div class="role-dashboard-wrapper" :data-testid="`role-home-${canonicalRole}`">
    <!-- Navigation Drawer (Sidebar Menu) -->
    <v-navigation-drawer
      v-model="drawer"
      temporary
      location="left"
      width="280"
    >
      <!-- User Profile Section -->
      <v-list-item class="pa-4 bg-primary">
        <template v-slot:prepend>
          <v-avatar color="white" size="56">
            <v-icon size="32" color="primary">mdi-account-circle</v-icon>
          </v-avatar>
        </template>
        <v-list-item-title class="text-h6 text-white font-weight-bold mb-1">
          {{ userDisplayName }}
        </v-list-item-title>
        <v-list-item-subtitle class="text-white opacity-80">
          {{ roleConfig.title }}
        </v-list-item-subtitle>
      </v-list-item>

      <v-divider></v-divider>

      <!-- Menu Items -->
      <v-list nav density="comfortable" class="py-2">
        <v-list-item
          v-for="item in visibleMenuItems"
          :key="item.route"
          :to="item.route"
          :prepend-icon="item.icon"
          :title="item.title"
          :active="isActiveRoute(item.route)"
          color="primary"
          rounded="xl"
          class="ma-2"
        >
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <v-divider></v-divider>
        <v-list nav density="comfortable">
          <!-- P13-V7B — "Configuración" retired: it closed the drawer and did nothing else. There is no
               settings section behind it for any role that renders this shell. -->
          <!-- MGI-PILOT-SELF-SERVICE-CREDENTIAL-ROTATION-WS1: offered to the sold roles only (the backend admits no other). -->
          <v-list-item
            v-if="canChangeOwnPassword"
            prepend-icon="mdi-lock-reset"
            title="Cambiar contraseña"
            color="grey-darken-1"
            rounded="xl"
            class="ma-2"
            data-testid="open-change-password"
            @click="changePasswordOpen = true"
          >
          </v-list-item>
          <v-list-item
            prepend-icon="mdi-logout"
            title="Cerrar Sesión"
            color="error"
            rounded="xl"
            class="ma-2"
            data-testid="role-shell-logout"
            @click="handleLogout"
          >
          </v-list-item>
        </v-list>
      </template>
    </v-navigation-drawer>

    <!-- Header común con personalización por rol -->
    <v-app-bar :color="roleConfig.primaryColor" elevation="3">
      <!-- Botón del menú lateral - Ahora funcional -->
      <v-app-bar-nav-icon data-testid="role-shell-menu" aria-label="Menú" @click="drawer = !drawer" />
      
      <v-app-bar-title>
        <div class="d-flex align-center">
          <v-icon :icon="roleConfig.icon" size="36" class="mr-3"></v-icon>
          <div>
            <h2 class="text-h5 font-weight-bold">{{ roleConfig.title }}</h2>
            <p class="text-caption mb-0 opacity-80">{{ roleConfig.subtitle }}</p>
          </div>
        </div>
      </v-app-bar-title>
      <v-spacer></v-spacer>
      
      <!-- Slot para acciones adicionales en el header -->
      <slot name="header-actions"></slot>
      
      <!-- P13-V7B — the status chip is retired. "En Ruta", "En Servicio" and "Zona Asignada" were
           literals of the role table, bound to no state: a route operator with no route was shown "En
           Ruta", an agency employee "En Servicio" on a page that declares attendance unavailable. -->
    </v-app-bar>

    <!-- Contenido del dashboard -->
    <v-main>
      <v-container fluid class="pa-2">
        <!-- KPIs Row
             P13-V7B (refutation): a card shows the value its producer stated — no client permission
             masks it any more (`RoleKpiCard` has no gate). The row is drawn when the SESSION's role has
             KPI cards; the agency employee loads none, so it needs no exclusion by name. -->
        <v-row class="mb-3" v-if="roleKPIs.length > 0">
          <v-col
            v-for="kpi in roleKPIs"
            :key="kpi.key"
            cols="12"
            :md="getKPIColumnSize()"
            class="pa-2"
          >
            <role-kpi-card
              :title="kpi.title"
              :value="kpi.value"
              :icon="kpi.icon"
              :color="kpi.color"
              :trend="kpi.trend"
            />
          </v-col>
        </v-row>

        <!-- Contenido específico del rol -->
        <slot name="role-content"></slot>

        <!-- P13-V7B (refutation): the floating action button is RETIRED. Its actions came from the
             per-role table of `useRolePermissions` (system_config, backup_system, assign_task, …),
             filtered by a permission list of that same table, and each one only emitted
             `actionExecuted`, which this shell never listened to: no action had a route, a handler or
             a backend capability behind it. With the table gone there is nothing left to offer, and
             nothing the drawer's admission rule (`admits`) could admit. -->
      </v-container>
    </v-main>

    <change-password-dialog v-if="canChangeOwnPassword" v-model="changePasswordOpen" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { getRedirectForRole } from '@/router/guards'
import { useRouteAdmission } from '@/composables/useRouteAdmission'
// P13-V7B (refutation): `useRolePermissions.js` (a legacy alias table plus a per-role permission and
// floating-action table) and `RoleFloatingActions.vue` are DELETED; nothing here consults a client
// permission list any more.
// @ts-ignore
import { useRoleData } from '@/composables/roles/useRoleData.js'
// @ts-ignore
import { useRoleKPIs } from '@/composables/roles/useRoleKPIs.js'
import RoleKpiCard from './RoleKpiCard.vue'
import ChangePasswordDialog from '@/components/account/ChangePasswordDialog.vue'
import { canSelfRotate } from '@/services/auth/selfCredentialRotation'

interface Props {
  role: string
  userId?: number
  groupId?: number
  agencyId?: number
}

const props = defineProps<Props>()

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

// State para el drawer
const drawer = ref(false)

// MGI-PILOT-SELF-SERVICE-CREDENTIAL-ROTATION-WS1: the signed-in user's own password change (sold roles only).
const changePasswordOpen = ref(false)
const canChangeOwnPassword = computed(() => canSelfRotate(authStore.userRole))

/**
 * P13-V7B (refutation) — THE SHELL'S ROLE IS A CANONICAL NAME OR THE SESSION'S.
 *
 * `role` is written by hand in the mounting view. It used to go through an alias table
 * (`supervisor_zona` → `field_supervisor`, `operador_ruta`, `empleado_agencia`, …) that lived beside a
 * per-role permission map and fed the endpoint table of `BaseRoleService`, keyed on those same legacy
 * names. That table is deleted. A prop that names a canonical role — one the single role→home
 * authority knows — is used as written; any other spelling is not a role name, and the shell falls
 * back to the role of the SESSION, which the server decided. A legacy alias therefore decides nothing.
 * It still only shapes what the drawer DECLARES: what it DRAWS is filtered by `admits` below.
 */
const isCanonicalRole = (role: string | null | undefined): boolean =>
  getRedirectForRole(String(role || '')) !== '/dashboard'
const canonicalRole = computed(() => (isCanonicalRole(props.role) ? props.role : (authStore.userRole || '')))

/**
 * P13-V7B (refutation) — WHAT THE HEADER SAYS, and nothing else. Title, subtitle, icon and colour used
 * to come from `useRolePermissions`, whose entries ALSO carried permission lists and floating actions.
 * Only the presentation survives, here: no entry grants, gates or offers anything.
 */
const ROLE_SHELL_PRESENTATION: Record<string, { title: string; subtitle: string; icon: string; primaryColor: string }> = {
  super_admin: { title: 'Super Administrador', subtitle: 'Control Total del Sistema', icon: 'mdi-shield-crown', primaryColor: 'deep-purple darken-2' },
  administration_manager: { title: 'Administrador', subtitle: 'Gestión Administrativa', icon: 'mdi-account-cog', primaryColor: 'blue darken-2' },
  group_manager: { title: 'Gerente de Grupo', subtitle: 'Gestion de grupos asignados', icon: 'mdi-account-group', primaryColor: 'blue darken-2' },
  field_supervisor: { title: 'Supervisor de Zona', subtitle: 'Gestión Operativa de Grupos', icon: 'mdi-account-supervisor-circle', primaryColor: 'indigo darken-1' },
  route_operator: { title: 'Operador de Ruta', subtitle: 'Seguimiento de Ruta', icon: 'mdi-truck', primaryColor: 'green darken-2' },
  agency_employee: { title: 'Empleado de Agencia', subtitle: 'Operaciones de Agencia', icon: 'mdi-office-building-marker', primaryColor: 'purple darken-2' },
  department_head: { title: 'Jefe de Departamento', subtitle: 'Gestión Departamental', icon: 'mdi-account-tie', primaryColor: 'indigo darken-2' }
}
const UNKNOWN_ROLE_PRESENTATION = { title: 'Rol sin permisos', subtitle: 'Sin accesos visuales disponibles', icon: 'mdi-account-off', primaryColor: 'grey darken-2' }
const roleConfig = computed(() => ROLE_SHELL_PRESENTATION[canonicalRole.value] || UNKNOWN_ROLE_PRESENTATION)

/**
 * P13-V7B (refutation) — the KPI strip and the role data follow the SESSION's role. They used to be
 * keyed on the hand-written prop (`supervisor_zona`, `operador_ruta`), which is how a legacy name
 * picked the endpoint. A figure is read only from a producer the backend admits for the role that
 * holds the session, whatever the mounting view wrote.
 */
const sessionRole = String(authStore.userRole || '')
const { loadRoleData } = useRoleData(sessionRole)
const { roleKPIs, loadKPIs } = useRoleKPIs(sessionRole)

// P13-V7B — the name in the drawer is the name of the SESSION. It used to read the localStorage key
// `mgi_user_name`, which nothing in the application ever writes, so every actor was called "Usuario".
const userDisplayName = computed(() => authStore.userName || '')

// Menú items dinámicos según el rol
const menuItems = computed(() => {
  const baseItems = []
  
  // Items comunes para todos los roles
  baseItems.push({
    title: 'Dashboard',
    icon: 'mdi-view-dashboard',
    route: getDashboardRoute()
  })

  // Items específicos para agency_employee
  if (canonicalRole.value === 'agency_employee') {
    baseItems.push({
      title: 'Mis Incidencias',
      icon: 'mdi-clipboard-list-outline',
      route: '/employee/incidents'
    })
    baseItems.push({
      title: 'Reportar Incidencia',
      icon: 'mdi-alert-plus',
      route: '/agency-employee?action=report'
    })
  }

  // MGI-PILOT-W2G · La Agenda queda FUERA del contrato del primer piloto: su superficie entregada es
  // vendor-only y el guard canonico deniega /agenda a las identidades de cliente. Anunciar aqui "Mi
  // Agenda" a route_operator, field_supervisor o group_manager ofrecia una capacidad que el backend
  // (perimetro W2-G sobre /api/agenda) responde 403. La entrada de super_admin se conserva.
  // Items específicos para route_operator
  if (canonicalRole.value === 'route_operator') {
    baseItems.push({
      title: 'Mis Incidencias',
      icon: 'mdi-alert-circle-outline',
      route: '/route-operator/incidents'
    })
  }

  // Items específicos para field_supervisor
  // NOTA: El supervisor tiene permisos con alcance ":zone" (su zona únicamente)
  // NO debe tener acceso a módulos globales como /incidents o /inventory (admin)
  if (canonicalRole.value === 'field_supervisor') {
    baseItems.push({
      title: 'Mis Incidencias',
      icon: 'mdi-clipboard-alert',
      route: '/supervisor/incidents'  // Módulo personalizado de zona
    })
    // W2 · ROLE-HOME-API-FIELD-SUPERVISOR — four entries were REMOVED here, and removed rather
    // than disabled:
    //
    //   Mapas de Campo      -> /supervisor/maps                     (V4 Routes/GPS, deferred)
    //   Solicitar Inventario -> /field-supervisor/inventory-request  (V2 Inventory, deferred)
    //   Gastos Fijos        -> /field-supervisor/fixed-expenses     (expenses, not sold)
    //   Reportes de Gastos  -> /field-supervisor/expense-reports    (expenses, not sold)
    //
    // Each was backed by role-home routes the W2 perimeter now contains to vendor-only, so every one
    // of them would have led a field supervisor to a page whose reads answer 403. §19 is explicit
    // that the repair for an unsold capability is not a disabled item or a "Sin datos" card: an
    // entry that still names the capability keeps advertising it. Unsold means not advertised.
    //
    // The same treatment W2G applied to "Mi Agenda" just above, for the same reason. The PAGES and
    // their components are left in place for their future owners (§20) — only client admission and
    // advertisement are contained; `super_admin` keeps them.
    // REMOVIDO: /incidents - Es el módulo administrativo global (AdvancedIncidentsManagement)
    // REMOVIDO: /inventory - Es el inventario centralizado (InventoryManagement)
  }

  // Items para otros roles (puedes expandir según necesites)
  // NOTA: route_operator ya tiene su dashboard en getDashboardRoute(), no duplicar

  // Items específicos para group_manager
  if (canonicalRole.value === 'group_manager') {
    baseItems.push({
      title: 'Reportes',
      icon: 'mdi-chart-bar',
      route: '/reports'
    })
  }

  if (canonicalRole.value === 'super_admin') {
    baseItems.push({
      title: 'Reportes',
      icon: 'mdi-chart-bar',
      route: '/reports'
    })
  }

  // Items específicos para super_admin
  if (canonicalRole.value === 'super_admin') {
    baseItems.push({
      title: 'Gestión de Usuarios',
      icon: 'mdi-account-group',
      route: '/users'
    })
    baseItems.push({
      title: 'Gestión de Grupos',
      icon: 'mdi-domain',
      route: '/super-admin/groups-management'
    })
    baseItems.push({
      title: 'Mi Agenda',
      icon: 'mdi-calendar-clock',
      route: '/agenda'
    })
    // 💰 Categoría de Gastos para SuperAdmin
    baseItems.push({
      title: 'Reportes de Gastos',
      icon: 'mdi-chart-box',
      route: '/super-admin/expense-reports'
    })
  }

  return baseItems
})

// P13-V7B — what the drawer DRAWS. `menuItems` above is keyed on the `role` PROP, a value the mounting
// view writes by hand; what a session may open is decided by the router. The drawer therefore shows an
// entry only when the route behind it renders a screen and admits the role of the SESSION — the same
// rule `canonicalGuard` applies. A shell mounted with a prop the session does not hold offers nothing.
const { admits } = useRouteAdmission()
const visibleMenuItems = computed(() => menuItems.value.filter((item) => admits(item.route)))

// P13-V7B-MIN C2 — the shell's "Dashboard" item consumes the CANONICAL role→home authority.
// This was a third independent role→home table (after guards.ts and the auth store). It is the map
// that decides where field_supervisor, route_operator and agency_employee are sent when they click
// "Dashboard", so a drift here would contradict their own landing. It now delegates.
const getDashboardRoute = () => getRedirectForRole(canonicalRole.value || '')

// Verificar si la ruta está activa
const isActiveRoute = (routePath: string) => {
  return route.path === routePath
}

// Manejar logout — delega en el contrato canónico del auth store.
// authStore.logout() invalida la sesión de backend (POST /api/auth/logout),
// limpia la sesión canónica del frontend y desconecta el WebSocket de la app.
// El componente NO debe duplicar esa lógica (sin limpieza manual de localStorage,
// sin redirección legacy a /auth/real-login).
const handleLogout = async () => {
  drawer.value = false
  await authStore.logout()
  router.push('/login')
}

// P13-V7B (refutation): `roleActions` is gone with the floating action button (see the template).

const getKPIColumnSize = () => {
  const kpiCount = roleKPIs.value.length
  if (kpiCount === 1) return 12
  if (kpiCount === 2) return 6
  if (kpiCount === 3) return 4  // 12/3 = 4, sin espacio sobrante
  if (kpiCount === 4) return 3
  return 2
}

onMounted(async () => {
  try {
    // Filtrar parámetros undefined antes de enviar
    const cleanParams = {}
    if (props.userId !== undefined) cleanParams.userId = props.userId
    if (props.groupId !== undefined) cleanParams.groupId = props.groupId  
    if (props.agencyId !== undefined) cleanParams.agencyId = props.agencyId
    
    const loadTasks: Promise<any>[] = []
    const taskLabels: string[] = []
    // P13-V7B (refutation): decided by the SESSION's role, like the reads themselves.
    const usesRoleSpecificRuntime = sessionRole === 'route_operator' || sessionRole === 'agency_employee'

    if (!usesRoleSpecificRuntime) {
      loadTasks.push(loadRoleData(cleanParams).catch(err => {
        console.warn('⚠️ Error cargando datos del rol:', err.message)
        return null
      }))
      taskLabels.push('Role Data')
    }

    if (sessionRole !== 'agency_employee') {
      loadTasks.push(loadKPIs(cleanParams).catch(err => {
        console.warn('⚠️ Error cargando KPIs:', err.message)
        return null
      }))
      taskLabels.push('KPIs')
    }

    const results = await Promise.allSettled(loadTasks)
    
    results.forEach((result, index) => {
      const type = taskLabels[index]
      if (result.status === 'rejected') {
        console.warn(`❌ ${type} falló:`, result.reason)
      }
    })
    
  } catch (error) {
    console.warn('⚠️ Error inicializando BaseRoleDashboard:', error.message)
  }
})

// Exponer métodos para que los componentes hijos puedan actualizar los KPIs
// Wrapper que fuerza refresh por defecto cuando se llama desde hijos
const refreshKPIs = async (params = {}) => {
  return await loadKPIs(params, { forceRefresh: true })
}

defineExpose({
  loadKPIs: refreshKPIs,  // Siempre forzar refresh cuando se llama externamente
  roleKPIs
})
</script>

<style scoped>
.role-dashboard-wrapper {
  min-height: 100vh;
  --v-layout-top: 25px;
}

.role-dashboard-wrapper .v-main {
  padding-top: 25px !important;
}

.opacity-80 {
  opacity: 0.8;
}
</style>
